import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { getBooks } from "@/lib/books";
import { Book } from "@/types/book";
import { useBookCacheInvalidation } from "./use-book-cache-invalidation";
import { useDeviceDetection } from "./use-device-detection";
import { preloadImages } from "@/lib/imageCache";

export function useBooks() {
  const { isIOS } = useDeviceDetection();

  // Increment this to invalidate old localStorage caches after backend changes
  const CACHE_VERSION = 3;
  const baseKey = isIOS ? "books-cache-ios" : "books-cache";
  const cacheKey = `${baseKey}-v${CACHE_VERSION}`;

  // iOS-specific cache strategy - shorter cache times for better reliability
  const staleTime = isIOS ? 2 * 60 * 1000 : 5 * 60 * 1000; // 2min on iOS, 5min elsewhere
  const gcTime = isIOS ? 10 * 60 * 1000 : 30 * 60 * 1000;   // 10min on iOS, 30min elsewhere
  
  // Optimize the query with better caching strategy
  const booksQuery = useQuery({
    // Include CACHE_VERSION so React Query fetches fresh data on version bump
    queryKey: ['books-list', isIOS ? 'ios' : 'web', CACHE_VERSION],
    queryFn: getBooks,
    staleTime,
    gcTime,
    refetchOnWindowFocus: false, // Don't refetch when window focus changes
    initialData: () => {
      // Try to get data from localStorage with versioned cache key
      try {
        const cachedDataStr = localStorage.getItem(cacheKey);
        if (cachedDataStr) {
          const parsed = JSON.parse(cachedDataStr);
          const { data, timestamp } = parsed || {};
          // Use shorter cache times on iOS
          const maxAge = isIOS ? 10 * 60 * 1000 : 30 * 60 * 1000;

          // Only use cache if it has actual books and is fresh
          if (Array.isArray(data) && data.length > 0 && typeof timestamp === "number") {
            if (Date.now() - timestamp < maxAge) {
              return data;
            }
          }
        }

        // Clean up any old unversioned keys to prevent stale data reappearing
        try {
          const oldKey = isIOS ? "books-cache-ios" : "books-cache";
          if (oldKey !== cacheKey) {
            localStorage.removeItem(oldKey);
          }
        } catch {
          // Ignore cleanup errors
        }
      } catch {
        // Error reading from cache is minor, don't log to console
      }
      return undefined;
    },
    meta: {
      onSuccess: (data: Book[]) => {
        // Cache successful responses to versioned localStorage key
        try {
          const cacheData = {
            data,
            timestamp: Date.now(),
            platform: isIOS ? 'ios' : 'web',
            version: CACHE_VERSION,
          };
          localStorage.setItem(cacheKey, JSON.stringify(cacheData));
          
          // Clear old unversioned cache keys if present
          const staleKeys = ['books-cache', 'books-cache-ios'];
          staleKeys.forEach((k) => {
            if (k !== cacheKey) {
              localStorage.removeItem(k);
            }
          });
        } catch {
          // Error saving to cache is minor, don't log to console
        }
      }
    }
  });
  const books = booksQuery.data || [];
  
  // Automatically invalidate image cache when books are updated
  useBookCacheInvalidation(books);
  
  // Preload cover images as soon as books data is available
  useEffect(() => {
    if (books.length > 0) {
      const coverUrls = books
        .map(b => b.cover_image_url)
        .filter((url): url is string => Boolean(url));
      
      // Preload covers with first 6 as high priority
      preloadImages(coverUrls, 6);
    }
  }, [books]);
  
  // Filter books by bundle status and bundle_id - ensuring proper filtering
  const activeBooks = Array.isArray(books) 
    ? books.filter((book: Book) => book.bundle?.status === 'active')
    : [];
  
  const upcomingBooks = Array.isArray(books)
    ? books.filter((book: Book) => book.bundle?.status === 'upcoming')
    : [];
  
  const expiredBooks = Array.isArray(books)
    ? books.filter((book: Book) => book.bundle?.status === 'expired')
    : [];

  // Filter preview books (books with is_preview = true)
  const previewBooks = Array.isArray(books)
    ? books.filter((book: Book) => book.is_preview === true)
    : [];

  // Helper function to get books by specific bundle ID
  const getBooksByBundleId = (bundleId: number) => {
    return Array.isArray(books) 
      ? books.filter((book: Book) => book.bundle_id === bundleId)
      : [];
  };
  
  return {
    activeBooks,
    upcomingBooks,
    expiredBooks,
    previewBooks,
    allBooks: books,
    getBooksByBundleId,
    isLoading: booksQuery.isLoading,
    isError: booksQuery.isError,
    error: booksQuery.error,
    refetch: booksQuery.refetch,
    isFetching: booksQuery.isFetching
  };
}

